<template>
  <div class="relative flex" ref="dropdownRef">
    <!-- Trigger -->
    <button
      @click="toggleDropdown"
      class="cursor-pointer"
    >
      <UiAvatar
        :url="clientAuthStore.profilePicture || undefined"
        :initials="initials"
        size="xxs"
      />
    </button>

    <div
      v-show="isOpen"
      class="absolute right-0 mt-2 w-[343px] bg-white border border-grey rounded-md shadow-lg z-50 p-4 flex flex-col items-start transition-opacity duration-200 ease-in-out select-none"
    >
      <!-- Profile Card Section -->
      <div class="w-full p-2 border border-grey rounded-md mb-4">
        <div class="flex items-center space-x-4">
          <UiAvatar
            :url="clientAuthStore.profilePicture || undefined"
            :initials="initials"
            size="sm"
          />

          <!-- Profile Info -->
          <div>
            <h3 class="text-h2 font-semibold">{{ clientAuthStore.displayName }}</h3>
            <p v-if="clientAuthStore.client?.company_name" class="text-h4 text-grey-dark font-normal">
              {{ clientAuthStore.client.company_name }}
            </p>
          </div>
        </div>
      </div>

      <!-- Menu Items -->
      <div class="text-primary w-full">
        <button
            v-if="shouldShowOldDashboardSwitch"
            @click="switchToOldDashboard"
            class="cursor-pointer flex items-center px-4 py-2 text-left gap-2 w-full hover:bg-gray-100 group"
        >
          <Overview class="w-5 h-5" />
          <div class="flex w-full justify-between">
            <span class="text-paragraph font-normal">New Dashboard</span>
            <div class="relative w-[46px] h-[24px]">
              <UiTooltip content="Switch to Old Dashboard">
                <SwitchOn class="absolute inset-0 opacity-100 transition-opacity duration-300" />
              </UiTooltip>
            </div>
          </div>
        </button>
        <router-link class="w-full" to="/client/settings" @click="isOpen = false">
          <button class="cursor-pointer w-full bg-white hover:bg-gray-100 px-4 flex items-center gap-2 py-2">
            <SettingsIcon class="w-5 h-5" />
            <span class="text-paragraph font-normal">Settings</span>
          </button>
        </router-link>
        <!-- <router-link class="w-full" to="/client/transactions" @click="isOpen = false">
          <button class="cursor-pointer w-full bg-white hover:bg-gray-100 px-4 flex items-center gap-2 py-2">
            <TransactionsIcon class="w-5 h-5" />
            <span class="text-paragraph font-normal">Transactions</span>
          </button>
        </router-link> -->
        <button @click="logout()" class="cursor-pointer w-full bg-white hover:bg-gray-100 px-4 flex items-center gap-2 py-2">
          <LogoutIcon class="w-5 h-5" />
          <span class="text-paragraph font-normal">Logout</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth.ts'
import { onClickOutside } from '@vueuse/core'
import { useClientAuthStore } from '@/app/stores/clientAuth'
import { UiAvatar, UiTooltip, generateInitials } from '@shopexperts/shared'
import { useUserVersion } from '@/app/composables/experts/useUserVersion'
import SettingsIcon from '@/assets/icons/settings.svg'
import LogoutIcon from '@/assets/icons/logout.svg'
import SwitchOn from '@/assets/icons/switch-on.svg'
import Overview from '@/assets/icons/overview.svg'

const router = useRouter()
const authStore = useAuthStore()
const clientAuthStore = useClientAuthStore()

const isOpen = ref(false)
const dropdownRef = ref(null)

// Use the user version composable to determine dashboard features
const { version: userVersion } = useUserVersion()

// Generate initials for avatar fallback
const initials = computed(() => {
  return generateInitials(clientAuthStore.fullName || 'User')
})

const shouldShowOldDashboardSwitch = computed(() => {
  return userVersion.value === null || userVersion.value <= 1
})

const switchToOldDashboard = async () => {
  try {
    const { data } = await authStore.switchToOldDashboard();
    window.location.href = data.url;
  } catch (err) {
    console.error('SSO switch failed', err);
  }
};

function toggleDropdown() {
  isOpen.value = !isOpen.value
}

onClickOutside(dropdownRef, () => {
  isOpen.value = false
})

const logout = async () => {
  authStore.logout()
  clientAuthStore.clearToken()
  await router.push('/login')
}
</script>
